'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Rings from '../_assets/rings.svg';
import { useEffect } from 'react';
import { AI_PROMPT_BOX_PLACEHOLDER } from '@/constant/Constants';
import { useExplorer } from '@/hooks/useExplorer';

const PlaceholderVariants = {
  hidden: {
    opacity: 0.3,
    y: -50,
    x: 50,
    scale: 0.95,
  },
  show: {
    opacity: 1,
    y: 0,
    x: 0,
    scale: 1,
    transition: {
      delay: 0.1,
      duration: 0.7,
      ease: 'easeInOut',
      type: 'spring',
      stiffness: 80,
      damping: 14,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    x: 30,
    scale: 0.95,
    transition: {
      duration: 0.6,
      ease: 'easeInOut',
    },
  },
};

export function PromptPlaceholder({
  setIsScrolledTop,
  textareaRef,
}: {
  setIsScrolledTop?: (value: boolean) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const { userPrompt } = useExplorer();
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    if (setIsScrolledTop) setIsScrolledTop(true);
  };
  useEffect(() => {
    if (window.scrollY !== 0) {
      if (setIsScrolledTop) setIsScrolledTop(false);
    }
  }, [setIsScrolledTop]);

  const handlePlaceholderClick = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    scrollToTop();
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="suggestions"
        initial="hidden"
        animate="show"
        exit="exit"
        variants={PlaceholderVariants}
      >
        <div
          onClick={handlePlaceholderClick}
          className="max-w-80 ml-2 flex gap-2 p-4 justify-start items-start border-white border-4 bg-gray-100 hover:bg-gray-200 rounded-2xl shadow-md mb-4 min-h-4 hover:scale-[1.02] transition-all duration-300 ease-in-out cursor-pointer"
        >
          <Image width={18} src={Rings} alt="recommendation" />
          {userPrompt ? (
            <p className="m-0 text-gray-700 text-ellipsis overflow-hidden whitespace-nowrap ">
              {userPrompt}
            </p>
          ) : (
            <p className="m-0 text-gray-400 text-sm text-ellipsis overflow-hidden whitespace-nowrap ">
              {AI_PROMPT_BOX_PLACEHOLDER}
            </p>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
